import React, { useState } from 'react';
import { LuMapPin, LuPhone, LuMail, LuClock } from 'react-icons/lu';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PageWrapper } from '../components/PageWrapper';
import PageHero from '../components/PageHero';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', checkIn: '', checkOut: '', guests: 2, roomPreference: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post('/api/inquiries', form);
      toast.success('Your inquiry has been sent! We\'ll respond within 24 hours.');
      setForm({ name: '', email: '', phone: '', checkIn: '', checkOut: '', guests: 2, roomPreference: '', message: '' });
    } catch {
      toast.error('Failed to send. Please try WhatsApp or email directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <Helmet>
        <title>Contact Us & Book Your Stay – Villa Tesori Wayanad</title>
        <meta name="description" content="Get in touch with Villa Tesori for room availability, bookings, and travel guidance in Wayanad. Located near Soojipara Falls, Meppady." />
        <meta name="keywords" content="Villa Tesori contact number, book homestay Wayanad, Wayanad homestay address, Meppadi homestay booking, luxury stay Wayanad contact" />
        <meta property="og:title" content="Contact Villa Tesori Homestay" />
        <meta property="og:description" content="Plan your perfect Wayanad getaway. Contact us for direct bookings and local recommendations." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://villatesori.com/contact" />
        <link rel="canonical" href="https://villatesori.com/contact" />
      </Helmet>

      <PageHero
        scriptText="Get in Touch"
        title="Contact & Bookings"
        subtitle="We'd love to host you — reach out and let's plan your perfect stay"
        bgImage="https://images.unsplash.com/photo-1448375240586-882707db888b?w=1600"
      />

      <section className="section">
        <div className="container">
          <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 80 }}>
            {/* Info */}
            <div>
              <div className="section-label">Reach Us</div>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.6rem', marginBottom: 16 }}>We're Here for You</h2>
              <div className="divider" />
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.9, marginBottom: 36 }}>
                Nimmy and Raphi personally respond to every inquiry. We'll help you plan your perfect Wayanad getaway, suggest the best activities, and ensure your stay is nothing short of magical.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginBottom: 40 }}>
                {[
                  { icon: <LuMapPin />, label: 'Address', value: 'Near Soojipara Waterfalls, Elavayal, Puthumala, Meppady, Vellarimala, Wayanad – 673577, Kerala, India' },
                  { icon: <LuPhone />, label: 'Phone / WhatsApp', value: <a href="https://wa.me/917558806677" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>+91 75588 06677</a> },
                  { icon: <LuMail />, label: 'Email', value: 'villatesori@gmail.com' },
                  { icon: <LuClock />, label: 'Response Time', value: 'Within 24 hours' },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', gap: 16 }}>
                    <span style={{ fontSize: '1.4rem', marginTop: 2, flexShrink: 0, color: 'var(--gold)' }}>{item.icon}</span>
                    <div>
                      <div style={{ fontSize: '0.72rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 4, fontFamily: 'Jost', fontWeight: 500 }}>{item.label}</div>
                      <div style={{ color: 'var(--text-body)', fontSize: '0.9rem', lineHeight: 1.6 }}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map embed placeholder */}
              <div style={{ height: 240, borderRadius: 2, overflow: 'hidden', background: 'var(--mist)', position: 'relative' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3917.6!2d76.05!3d11.56!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTHCsDMzJzM2LjAiTiA3NsKwMDMnMDAuMCJF!5e0!3m2!1sen!2sin!4v1"
                  width="100%" height="100%" style={{ border: 0 }}
                  allowFullScreen loading="lazy"
                  title="Villa Tesori Map"
                />
              </div>

              <div style={{ marginTop: 24, padding: '20px 24px', background: 'var(--cream)', borderRadius: 2, border: '1px solid rgba(201,168,76,0.2)' }}>
                <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>
                  <strong style={{ color: 'var(--forest)' }}>Getting Here:</strong> 13 km from Meppadi Town. We recommend arriving before sunset for the best views and to safely navigate the hill roads. Airport: Calicut (CCJ) – 82km.
                </p>
              </div>
            </div>

            {/* Form */}
            <div>
              <div className="contact-form-box" style={{ background: 'var(--white)', padding: 'clamp(24px,5vw,48px) clamp(20px,4vw,44px)', borderRadius: 2, border: '1px solid var(--mist)' }}>
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', marginBottom: 8 }}>Send an Inquiry</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 32 }}>Fill in your details and we'll get back to you with availability and rates.</p>
                <form onSubmit={handleSubmit}>
                  <div className="grid-2" style={{ gap: 20 }}>
                    <div className="form-group">
                      <label className="form-label">Full Name *</label>
                      <input className="form-control" name="name" value={form.name} onChange={handleChange} required placeholder="Your name" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone / WhatsApp</label>
                      <input className="form-control" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <input className="form-control" type="email" name="email" value={form.email} onChange={handleChange} required placeholder="your@email.com" />
                  </div>
                  <div className="grid-2" style={{ gap: 20 }}>
                    <div className="form-group">
                      <label className="form-label">Check-in Date</label>
                      <input className="form-control" type="date" name="checkIn" value={form.checkIn} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Check-out Date</label>
                      <input className="form-control" type="date" name="checkOut" value={form.checkOut} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="grid-2" style={{ gap: 20 }}>
                    <div className="form-group">
                      <label className="form-label">No. of Guests</label>
                      <select className="form-control" name="guests" value={form.guests} onChange={handleChange}>
                        {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n} {n===1?'Guest':'Guests'}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Room Preference</label>
                      <select className="form-control" name="roomPreference" value={form.roomPreference} onChange={handleChange}>
                        <option value="">Any Room</option>
                        <option>Chembra View Suite</option>
                        <option>Tea Garden Deluxe</option>
                        <option>Valley Mist Cottage</option>
                        <option>Forest Stream Premium</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Message / Special Requests *</label>
                    <textarea className="form-control" name="message" value={form.message} onChange={handleChange} required placeholder="Tell us about your visit, any special occasions, dietary needs, or questions..." style={{ minHeight: 120 }} />
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
                    {loading ? 'Sending...' : 'Send Inquiry →'}
                  </button>
                  <p style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 16 }}>
                    We respond within 24 hours. For urgent bookings, WhatsApp us directly.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
